/**
 * Reordering „Pytania własne" — one dispatch that the pointer, the keyboard and the ⋯ menu share.
 *
 * Shaped after `features/plan-day-programming/lib/useExerciseMove`, which is the app's reference
 * consumer of `shared/ui/dnd`, and for its reasons:
 *
 * · **One monitor, bound once.** A monitor rebound between `onDragStart` and `onDrop` never sees the
 *   drop. The handlers it calls are kept in a ref written from an effect, which is what lets it stay
 *   bound while the list underneath it changes.
 * · **A release over nothing is a CANCEL**, not a silent no-op. Only a monitor sees that; a
 *   per-target `onDrop` never fires for it. View ADR §11.
 * · **The edge-to-position arithmetic is `model/editorDraft`'s `dropPosition`**, so the one place an
 *   edge becomes a position is a pure function a test can reach. It is two conversions, not one —
 *   an edge is a gap, and a gap is not a position once the question has been lifted out of the list.
 * · **Every outcome is announced**, including the refusal, because a drag nobody can see the result
 *   of is a drag a screen-reader user cannot use.
 *
 * The keyboard path is not a reduced fallback — it dispatches the same `move` the drop does, through
 * `shared/ui/dnd/useKeyboardMove`, and so does the row's ⋯ menu.
 */

import { useEffect, useRef } from 'react';

import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';

import { dropPosition } from '../model/editorDraft';
import type { TranslateKey } from '../model/translateKey';

/** The payload a question row puts on the drag, and the one its drop target carries. */
export const QUESTION_DRAG_KIND = 'reportsCustomQuestion';

export interface QuestionMove {
  /** Move the question with this id to this position among the custom questions. */
  readonly move: (id: string, targetIndex: number, label: string) => void;
}

export function useQuestionMove(options: {
  readonly t: TranslateKey;
  readonly count: number;
  readonly onMove: (id: string, targetIndex: number) => void;
  readonly announce: (message: string) => void;
}): QuestionMove {
  const { t, count, onMove, announce } = options;

  const move = (id: string, targetIndex: number, label: string): void => {
    const bounded = Math.min(Math.max(targetIndex, 0), Math.max(0, count - 1));
    onMove(id, bounded);
    announce(
      t('web:reports.editor.dnd.moved', { label, position: bounded + 1, count }),
    );
  };

  // The latest handler, so the monitor binds ONCE. Written in an EFFECT rather than during render —
  // the React Compiler rejects a ref written while rendering, and the only reader is a drop callback.
  const latest = useRef({ move, announce, t });
  useEffect(() => {
    latest.current = { move, announce, t };
  });

  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) => source.data.kind === QUESTION_DRAG_KIND,
        onDrop: ({ source, location }) => {
          const carried = source.data;
          const id = typeof carried.id === 'string' ? carried.id : null;
          const label = typeof carried.label === 'string' ? carried.label : '';
          // Where the question was picked up FROM. A gap below its own place shifts up by one when
          // the move lifts it out, so the destination cannot be resolved without it.
          const from = typeof carried.index === 'number' ? carried.index : null;
          if (id === null || from === null) return;

          const target = location.current.dropTargets[0];
          if (target === undefined) {
            // Released over nothing at all. Said out loud rather than passed over in silence.
            latest.current.announce(latest.current.t('web:reports.editor.dnd.cancelled', { label }));
            return;
          }

          const position = resolveTarget(target.data, from);
          if (position === null) return;
          latest.current.move(id, position, label);
        },
      }),
    [],
  );

  return { move };
}

/** A drop target's payload as the position the carried question ends up at. */
function resolveTarget(data: Record<string | symbol, unknown>, from: number): number | null {
  if (data.kind !== QUESTION_DRAG_KIND) return null;
  const index = data.index;
  if (typeof index !== 'number') return null;
  const edge = extractClosestEdge(data);
  return dropPosition({
    from,
    targetIndex: index,
    edge: edge === 'top' || edge === 'bottom' ? edge : null,
  });
}
